export const TEAM_DEFAULT_AVATAR = "/img/user_avatar.jpg";

export function safeArray(value) {
  return Array.isArray(value) ? value : [];
}

export function toNumber(value, defaultValue = 0) {
  const number = Number(value);
  return Number.isFinite(number) ? number : defaultValue;
}

export function normalizeText(value = "") {
  return String(value)
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase()
    .trim();
}

export function stripHtml(value = "") {
  return String(value).replace(/<[^>]*>/g, "").trim();
}

export function sortTeamByPosition(rows) {
  return safeArray(rows)
    .slice()
    .sort((a, b) => {
      const positionA = toNumber(a?.position, 0);
      const positionB = toNumber(b?.position, 0);

      if (positionA !== positionB) return positionA - positionB;

      const nameA = `${a?.nom || ""} ${a?.prenom || ""}`;
      const nameB = `${b?.nom || ""} ${b?.prenom || ""}`;

      return nameA.localeCompare(nameB, "fr");
    });
}

export function normalizeOrderTeam(rows) {
  return sortTeamByPosition(rows).map((member, index) => ({
    ...member,
    position: index + 1,
  }));
}

export function getTeamCategories(team) {
  const categories = new Set();

  for (const member of safeArray(team)) {
    const category = String(member?.categorie || "").trim();
    if (category) categories.add(category);
  }

  return Array.from(categories).sort((a, b) => a.localeCompare(b, "fr"));
}

export function filterTeam(team, filters) {
  const query = normalizeText(filters?.query || "");
  const category = String(filters?.categorie || "").trim();

  return sortTeamByPosition(team).filter((member) => {
    if (category && String(member?.categorie || "") !== category) {
      return false;
    }

    if (!query) return true;

    const searchable = normalizeText(
      `${member?.nom || ""} ${member?.prenom || ""} ${
        member?.fonction || ""
      } ${member?.categorie || ""} ${member?.mail_member || ""} ${
        member?.tel_member || ""
      } ${stripHtml(member?.bio_member || "")}`
    );

    return searchable.includes(query);
  });
}

export function calculateTeamStats(team) {
  const rows = safeArray(team);

  const categories = new Set();
  let withPhoto = 0;
  let withoutPhoto = 0;
  let withEmail = 0;
  let withPhone = 0;
  let withBio = 0;

  for (const member of rows) {
    if (member?.categorie) categories.add(member.categorie);

    if (String(member?.photoURL || "").trim()) withPhoto += 1;
    else withoutPhoto += 1;

    if (String(member?.mail_member || "").trim()) withEmail += 1;
    if (String(member?.tel_member || "").trim()) withPhone += 1;
    if (stripHtml(member?.bio_member || "")) withBio += 1;
  }

  return {
    total: rows.length,
    categoriesCount: categories.size,
    withPhoto,
    withoutPhoto,
    withEmail,
    withPhone,
    withBio,
  };
}

export function getNextTeamPosition(team) {
  return safeArray(team).reduce((max, member) => {
    return Math.max(max, toNumber(member?.position, 0));
  }, 0) + 1;
}

export function getDefaultTeamForm(position = 1) {
  return {
    id: "",
    nom: "",
    prenom: "",
    fonction: "",
    photoURL: "",
    categorie: "",
    position,
    mail_member: "",
    tel_member: "",
    bio_member: "",
  };
}

export function mapTeamMemberToForm(member, position = 1) {
  if (!member) return getDefaultTeamForm(position);

  return {
    id: member?.id || "",
    nom: member?.nom || "",
    prenom: member?.prenom || "",
    fonction: member?.fonction || "",
    photoURL: member?.photoURL || "",
    categorie: member?.categorie || "",
    position: member?.position ?? position,
    mail_member: member?.mail_member || "",
    tel_member: member?.tel_member || "",
    bio_member: member?.bio_member || "",
  };
}

export function buildTeamMemberPayload(formData) {
  return {
    nom: String(formData?.nom || "").trim(),
    prenom: String(formData?.prenom || "").trim(),
    fonction: String(formData?.fonction || "").trim(),
    photoURL: String(formData?.photoURL || "").trim(),
    categorie: String(formData?.categorie || "").trim(),
    position: toNumber(formData?.position, 0),
    mail_member: String(formData?.mail_member || "").trim(),
    tel_member: String(formData?.tel_member || "").trim(),
    bio_member: String(formData?.bio_member || ""),
  };
}

export function validateTeamMemberPayload(payload) {
  if (!payload.nom) return "Le nom du membre est requis.";
  if (!payload.prenom) return "Le prénom du membre est requis.";
  if (!payload.fonction) return "La fonction est requise.";
  if (!payload.categorie) return "La catégorie est requise.";

  if (!Number.isFinite(Number(payload.position))) {
    return "La position doit être un nombre.";
  }

  return "";
}

export function validateImageFile(file) {
  if (!file) return "";

  const validType = /^image\/(png|jpe?g|webp)$/i.test(file.type);

  if (!validType) {
    return "Format image non supporté. Utilisez PNG, JPG ou WEBP.";
  }

  if (file.size > 10 * 1024 * 1024) {
    return "Image trop volumineuse. Taille maximale : 10 Mo.";
  }

  return "";
}

export function extractCreatedTeamMemberId(response) {
  return (
    response?.id ||
    response?.member?.id ||
    response?.teamMember?.id ||
    response?.data?.id ||
    response?.data?.member?.id ||
    response?.data?.teamMember?.id ||
    null
  );
}